import {
  guideQueryOptions,
  useGuideDeleteMutation,
  useGuideFavoriteMutation,
  useGuideRestoreMutation,
} from '@blesk-frontend/api';
import {
  Button,
  Close,
  cn,
  GuideView,
  ModalConfirm,
  Page,
  Star,
  Tooltip,
  Topbar,
  Trash,
  useModal,
} from '@blesk-frontend/ui';
import {useQuery} from '@tanstack/react-query';
import {Link} from '@tanstack/react-router';
import {ChevronLeft} from 'lucide-react';
import {useEffect} from 'react';
import {toast} from 'sonner';

import {BillingDiscountModal} from '@/features/billing';
import {GuideShareModal, GuideSkeleton} from '@/features/guides';
import {Route} from '@/routes/_app/guides/$guideId.index';

export const GuideDetailPage = () => {
  const navigate = Route.useNavigate();
  const searchParams = Route.useSearch();
  const {data: guide, isLoading, refetch} = useQuery(guideQueryOptions(Route.useParams().guideId));
  const {mutateAsync: mutateAsyncFavorite} = useGuideFavoriteMutation();
  const {mutateAsync: guideDeleteMutateAsync} = useGuideDeleteMutation();
  const {mutateAsync: guideRestoreMutateAsync} = useGuideRestoreMutation();

  const onClickBack = () => {
    if (searchParams.collectionId) {
      navigate({
        to: '/collections/$collectionId',
        params: {collectionId: searchParams.collectionId},
      });
    } else {
      navigate({to: '/guides'});
    }
  };

  const editLink = `/guides/${guide?.id}/edit`;

  const openConfirmDeleteModal = useModal((props) => (
    <ModalConfirm
      {...props}
      title="Подтвердите удаление"
      message="Удалённая инструкция будет перемещена в корзину."
      labels={{cancel: 'Отменить', confirm: 'Удалить'}}
      onConfirm={async () => {
        guide && (await guideDeleteMutateAsync(guide));
        await navigate({to: '/guides'});
      }}
    />
  ));

  const onClickRestore = async () => {
    if (!guide) return;
    return toast.promise(guideRestoreMutateAsync(guide), {
      loading: 'Восстановление...',
      error: 'При восстановлении произошла ошибка',
      success: () => {
        refetch();
        return 'Инструкция восстановлена';
      },
    });
  };

  const onClickFavorite = () => {
    if (!guide) return;
    return toast.promise(mutateAsyncFavorite(guide.id), {
      loading: 'Сохранение...',
      error: 'При добавлении в избранное произошла ошибка',
      success: (data) => (data.isFavorite ? 'Добавлено в избранное' : 'Удалено из избранного'),
    });
  };

  const openGuideShareModal = useModal(({onClose, ...props}) => (
    <GuideShareModal
      guideId={guide?.id}
      {...props}
      onClose={() => {
        onClose?.();
        navigate({search: {...searchParams, export: undefined}});
      }}
    />
  ));

  const openBillingDiscountModal = useModal<{discountId: string}>((props) => (
    <BillingDiscountModal {...props} />
  ));

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  useEffect(() => {
    setTimeout(() => {
      if (searchParams.discountId) {
        openBillingDiscountModal({discountId: searchParams.discountId});
      }
    }, 100);
  }, [searchParams.discountId]);

  if (isLoading || !guide) {
    return <GuideSkeleton />;
  }

  return (
    <Page>
      <Topbar variant="sticky">
        <div className="inline-flex items-center gap-1 text-paragraph-sm text-text-secondary">
          <Button variant="outline" size="icon" onClick={onClickBack}>
            <ChevronLeft className="size-4" />
          </Button>
        </div>

        {guide.deletedAt ? (
          <div className="ml-auto text-text-secondary">
            <Button variant="outline" onClick={onClickRestore}>
              Восстановить
            </Button>
          </div>
        ) : (
          <div className="ml-auto flex items-center gap-4 text-text-secondary">
            <Tooltip
              className="bg-bg-surface text-bg-surface"
              alwaysShow={true}
              showArrow={true}
              enabled={!!searchParams.export}
              placement="bottom-start"
              distance={{crossAxis: 16, mainAxis: 8}}
              color="bg-secondary"
              strategy="fixed"
              message={
                <div className="flex items-center gap-3 py-1 pl-1 text-paragraph-sm text-text-contrast">
                  <p>
                    Сохраните инструкцию в удобном формате
                    <br />
                    или поделитесь ссылкой на неё
                  </p>
                  <div
                    className="cursor-pointer"
                    onClick={() => navigate({search: {...searchParams, export: undefined}})}
                  >
                    <Close className="size-6" />
                  </div>
                </div>
              }
            >
              <Button
                onClick={() => {
                  openGuideShareModal();
                }}
              >
                Поделиться
              </Button>
            </Tooltip>

            <Link to={editLink}>
              <Button variant="outline">Редактировать</Button>
            </Link>
            <Tooltip
              message={
                guide._count.favorite ? <p>Удалить из избранного</p> : <p>Добавить в избранное</p>
              }
            >
              <Button variant="outline" size="icon" onClick={onClickFavorite}>
                <Star
                  className={cn('size-5', guide._count.favorite && 'text-attention-secondary')}
                  variant={guide._count.favorite ? 'fill' : 'default'}
                />
              </Button>
            </Tooltip>
            <Button variant="outline" size="icon" onClick={() => openConfirmDeleteModal()}>
              <Trash className="size-5" />
            </Button>
          </div>
        )}
      </Topbar>

      <GuideView guide={guide} />
    </Page>
  );
};
